import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {css} from "@emotion/react";
import {Col, Container, Row} from "@pg-design/grid";
import {
    calculateRemSize,
    flex,
    flexAlignCenter,
    flexDirection,
    listUnStyled,
    mb,
    onDesktop,
    p,
    ph,
    w100
} from "@pg-design/helpers-css";
import {BrandChartIcon, BrandClockIcon, BrandHandclapIcon} from "@pg-design/icons";
import {Picture} from "@pg-design/picture";
import {Text} from "@pg-design/text";

import {breakpoints} from "../../../common/app/styles/breakpoints";
import {flexShrink} from "../../../components/css_helpers/flex";
import {LANDING_WIDE_COL_PROPS} from "./landingMarketNews.constants";

import sectionBg from "../assets/big_data_behind_bg.png";
import teamPhoto from "../assets/big_data_behind_team.png";
import lightBulbIcon from "../assets/brand_light_bulb_icon.svg";
import teamAnalystIcon from "../assets/team_analyst_icon.svg";

const ICON_COLOR = "#242424";

const POINT_KEYS = [
    "landing.behind.point_1",
    "landing.behind.point_2",
    "landing.behind.point_3",
    "landing.behind.point_4",
    "landing.behind.point_5"
] as const;

const TEAM_PHOTO = {width: 601, height: 357};

const pointIcon = css`
    ${flexShrink(0)};
    width: 36px;
    height: 36px;
`;

const brandIconProps = {
    size: "3.6",
    wrapperSize: "3.6",
    wrapperColor: "transparent",
    fill: ICON_COLOR,
    css: pointIcon
} as const;

export const BigDataBehind = () => {
    const {t} = useTranslation();

    const points = useMemo(
        () => [
            {
                key: POINT_KEYS[0],
                icon: <img src={teamAnalystIcon} alt="" width={36} height={36} css={pointIcon} />
            },
            {
                key: POINT_KEYS[1],
                icon: <BrandClockIcon {...brandIconProps} />
            },
            {
                key: POINT_KEYS[2],
                icon: <BrandChartIcon {...brandIconProps} />
            },
            {
                key: POINT_KEYS[3],
                icon: <img src={lightBulbIcon} alt="" width={36} height={36} css={pointIcon} />
            },
            {
                key: POINT_KEYS[4],
                icon: <BrandHandclapIcon {...brandIconProps} />
            }
        ],
        []
    );

    return (
        <Container as="section" css={section}>
            <Row>
                <Col {...LANDING_WIDE_COL_PROPS}>
                    <div css={panel}>
                        <div css={inner}>
                            <div css={imageCol}>
                                <Picture
                                    css={teamImage}
                                    sources={[{minWidthPX: 0, src: teamPhoto, ...TEAM_PHOTO}]}
                                    alt=""
                                    loading="lazy"
                                />
                            </div>

                            <div css={contentCol}>
                                <Text css={mb(3)} as="h2" variant="headline_1">
                                    {t("landing.behind.title")}
                                </Text>

                                <ul css={pointsList}>
                                    {points.map(({key, icon}) => (
                                        <li css={pointItem} key={key}>
                                            <div css={pointRow}>
                                                {icon}
                                                <Text variant="body_copy_0">{t(key)}</Text>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

const section = css`
    ${w100};
    ${ph(1)};
    ${mb(4)};
`;

const panel = css`
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    background:
        linear-gradient(0deg, rgba(155, 215, 255, 0.2) 0%, rgba(155, 215, 255, 0.2) 100%),
        linear-gradient(0deg, rgba(255, 255, 255, 0.68) 0%, rgba(255, 255, 255, 0.68) 100%),
        url(${sectionBg}) lightgray 50% / cover no-repeat;
`;

const inner = css`
    position: relative;
    ${flex()};
    ${flexDirection("column")};
    ${p(3)};
    gap: ${calculateRemSize(3)};

    @media (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: center;
        ${p(4)};
        gap: ${calculateRemSize(4)};
    }
`;

const imageCol = css`
    ${flexShrink(0)};
    width: fit-content;
    max-width: 100%;
`;

const teamImage = css`
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
`;

const contentCol = css`
    flex: 1 1 auto;
    min-width: 0;
`;

const pointRow = css`
    ${flexAlignCenter};
    gap: ${calculateRemSize(2)};
`;

const pointItem = css`
    display: flex;
    flex-direction: column;
    padding-bottom: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid #fff;

    &:last-child {
        border: none;
    }
`;

const pointsList = css`
    ${listUnStyled};
    margin: 0;
    ${w100};
`;
